import React from "react";
import styled from "styled-components/macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@duocards/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import MammothNew from "icons/MammothNew";
import Confetti from "react-confetti";
import OnboardingButton from "./OnboardingButton";
import { useSeeHint } from "components/hint/Hint";
import { SWIPINGDONE } from "../steps/Verdict";
import { MAIN } from "root/RootRouter.config";
import { useNavigate } from "react-router-dom";
import { FIVEHINT } from "root/main/main/Main";
import DialogFullWindow from "components/other/DialogFullWindow";
import { TutorialFC } from "../TutorialContent";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  max-width: 400px;
  margin: 0 auto;
  width: 100%;
  height: 100%;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 340px;
  margin-top: 50px;
`;

const MammothIcon = styled(MammothNew)`
  width: 84px;
  height: 84px;
  flex-shrink: 0;
  transform: scaleX(-1);
`;

const HeaderText = styled.div`
  font-family: "Noto Sans", sans-serif;
  font-weight: 900;
  font-size: 20px;
  line-height: 1.362;
  text-align: center;
  color: #333333;
  width: 100%;
`;

const StepsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  margin-bottom: auto;
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 350px;
  position: relative;
`;

const StepItem = styled.div<{ $completed: boolean; $isCurrent: boolean; $isFuture: boolean }>`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 0 20px;
  border-radius: 15px;
  background: transparent;
  transition: all 0.3s ease;
  position: relative;
`;

const StepCircle = styled.div<{ $completed: boolean; $isCurrent: boolean; $isFuture: boolean }>`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
  font-size: 18px;
  color: ${({ theme, $completed, $isCurrent, $isFuture }) =>
    $completed ? theme.duo.color.white : $isCurrent ? "#00A1E6" : $isFuture ? "#C1DCFB" : theme.duo.color.text};
  background: ${({ theme, $completed }) => ($completed ? "#57BC57" : theme.duo.color.white)};
  border: ${({ $completed, $isCurrent, $isFuture }) =>
    $completed ? "none" : $isCurrent ? "2px solid #00A1E6" : $isFuture ? "1px solid #C1DCFB" : "2px solid #00A1E6"};
  transition: all 0.3s ease;
  flex-shrink: 0;
  position: relative;
  z-index: 2;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.duo.color.white};
  }
`;

const ConnectingLine = styled.div<{
  $isLast: boolean;
  $currentStepCompleted: boolean;
  $nextStepCompleted: boolean;
  $isCurrent: boolean;
}>`
  position: absolute;
  left: 45px;
  top: 31px;
  width: 3px;
  height: 103px;
  background: ${({ $currentStepCompleted, $nextStepCompleted }) =>
    $currentStepCompleted && $nextStepCompleted ? "#57BC57" : $currentStepCompleted ? "#00A1E6" : "#C1E4FF"};
  z-index: 1;
  display: ${({ $isLast }) => ($isLast ? "none" : "block")};
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StepTitle = styled.h3<{ $completed: boolean; $isCurrent: boolean; $isFuture: boolean }>`
  margin: 0;
  font-size: 18px;
  font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
  line-height: 1.2;
  color: ${({ $completed, $isCurrent, $isFuture }) =>
    $completed ? "#AAAAAA" : $isCurrent ? "#333333" : $isFuture ? "#AAAAAA" : "#333333"};
`;

const StepDescription = styled.p<{ $completed: boolean; $isCurrent: boolean; $isFuture: boolean }>`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #757575;
  line-height: 1.2;
`;

type Props = {
  currentStep?: number;
};

const OnboardingPlan: React.FC<Props> = ({ currentStep = 1 }) => {
  const { t } = useTranslation();
  const seeHint = useSeeHint();
  const navigate = useNavigate();

  const steps = [
    {
      id: 1,
      title: t("Introduction"),
      description: "",
      completed: currentStep > 1
    },
    {
      id: 2,
      title: t("Pick your first cards"),
      description: t("Each card is a phrase you'll learn. Pick cards from courses, YouTube videos, or add your own."),
      completed: currentStep > 2
    },
    {
      id: 3,
      title: t("Learn the cards"),
      description: t("Cards will come back at just the right time, so you never forget them."),
      completed: currentStep > 3
    }
  ];

  // Get header text based on current step
  const getHeaderText = () => {
    if (currentStep === 2) {
      return t("Your custom learning plan is ready!");
    } else if (currentStep === 3) {
      return t("Perfect! Now let's learn the cards you picked.");
    } else if (currentStep > 3) {
      return (
        <>
          {t("Good job!")}
          <br />
          {t("You learned your first cards!")}
        </>
      );
    }
    return t("Your custom learning plan is ready!");
  };

  const onClickContinue = () => {
    if (currentStep === 3) {
      seeHint(FIVEHINT);
      navigate(MAIN.url(), { replace: true });
    } else if (currentStep > steps.length) {
      seeHint(FIVEHINT);
      seeHint(SWIPINGDONE);
    }
  };

  return (
    <Wrap>
      {currentStep > steps.length && (
        <Confetti
          confettiSource={{ x: 0, y: 0, w: window.innerWidth, h: 150 }}
          numberOfPieces={80}
          gravity={0.15}
          recycle={false}
          opacity={0.7}
          style={{ zIndex: 1310, position: "fixed", top: 0, left: 0 }} // right behind snackbar
        />
      )}
      <HeaderSection>
        <MammothIcon />
        <HeaderText>{getHeaderText()}</HeaderText>
      </HeaderSection>
      <StepsWrapper>
        <StepsContainer>
          {steps.map((step, index) => {
            const isCurrent = step.id === currentStep;
            const isFuture = step.id > currentStep;
            const isLast = index === steps.length - 1;
            const nextStep = steps[index + 1];
            const nextStepCompleted = nextStep ? nextStep.completed : false;

            return (
              <StepItem key={step.id} $completed={step.completed} $isCurrent={isCurrent} $isFuture={isFuture}>
                <StepCircle $completed={step.completed} $isCurrent={isCurrent} $isFuture={isFuture}>
                  {step.completed ? <FontAwesomeIcon icon={faCheck} /> : step.id}
                </StepCircle>
                <ConnectingLine
                  $isLast={isLast}
                  $currentStepCompleted={step.completed}
                  $nextStepCompleted={nextStepCompleted}
                  $isCurrent={isCurrent}
                />
                <StepContent>
                  <StepTitle $completed={step.completed} $isCurrent={isCurrent} $isFuture={isFuture}>
                    {step.title}
                  </StepTitle>
                  {isCurrent && (
                    <StepDescription $completed={step.completed} $isCurrent={isCurrent} $isFuture={isFuture}>
                      {step.description}
                    </StepDescription>
                  )}
                </StepContent>
              </StepItem>
            );
          })}
        </StepsContainer>
      </StepsWrapper>
      {currentStep > 2 && <OnboardingButton onClick={onClickContinue}>{t("Continue")}</OnboardingButton>}
    </Wrap>
  );
};

export const OnboardingPlanWithDialog: TutorialFC = () => {
  return (
    <DialogFullWindow open={true} background={true} maxWidth={600}>
      <OnboardingPlan currentStep={4} />
    </DialogFullWindow>
  );
};

export default OnboardingPlan;
